/**
 * Parse the JWT and validate it.
 *
 * We are just checking that the signature is valid, but you can do more that.
 * For example, check that the payload has the expected entries or if the signature is expired..
 *
 * @param encodedToken
 * @param jwkData
 * @param options
 * @returns DecodedJwt | DecodedJwt & { userInfo: Auth0UserInfo }
 * @throws Error<message with reason>
 */
export async function validateJwt(encodedToken, jwkData, options = {}) {
    if (encodedToken == null)
        throw new Error('No token provided');
    if (typeof encodedToken !== 'string')
        throw new Error('provided token is not a string');
    const token = encodedToken.slice(0, 6) !== 'Bearer'
        ? encodedToken
        : encodedToken.slice(6).trim();
    const decodedToken = decodeJwt(token);
    const isValid = await isValidJwtSignature(decodedToken, jwkData);
    if (!isValid)
        throw new Error('Invalid token');
    if (!options.ignoreExpired && decodedToken.isExpired)
        throw new Error('Token is expired');
    return decodedToken;
}
/**
 * Parse and decode a JWT.
 * A JWT is three, base64 encoded, strings concatenated with ‘.’:
 *   a header, a payload, and the signature.
 * The signature is “URL safe”, in that ‘/+’ characters have been replaced by ‘_-’
 *
 * Steps:
 * 1. Split the token at the ‘.’ character
 * 2. Base64 decode the individual parts
 * 3. Retain the raw Bas64 encoded strings to verify the signature
 */
export function decodeJwt(token) {
    const parts = token.split('.');
    if (parts.length !== 3) {
        throw new Error('Invalid JWT format');
    }
    // Ensure all parts exist
    if (!parts[0] || !parts[1] || !parts[2]) {
        throw new Error('Invalid JWT format');
    }
    try {
        const header = JSON.parse(atob(parts[0]));
        const payload = JSON.parse(atob(parts[1]));
        const signature = atob(parts[2].replace(/_/g, '/').replace(/-/g, '+'));
        const now = Math.floor(Date.now() / 1000);
        const secondsLeft = payload.exp - now;
        const isExpired = secondsLeft <= 0;
        return {
            header: header,
            payload: payload,
            signature: signature,
            raw: { header: parts[0], payload: parts[1], signature: parts[2] },
            isExpired,
            expiresIn: isExpired ? null : secondsLeft,
        };
    }
    catch {
        throw new Error('Invalid token');
    }
}
/**
 * Validates a JWT signature against a JWKS (JSON Web Key Set).
 *
 * This function intentionally ignores the JWT "kid" header and instead attempts
 * signature verification against all candidate signing keys in the provided JWKS
 * until one yields a valid signature. This is a deliberate design choice (not fail-open)
 * to handle key rotation gracefully and avoid tight coupling to specific key IDs.
 *
 * Candidate keys are filtered to only include:
 * - Keys with use: 'sig' or no use specified
 * - Keys with kty: 'RSA', 'EC', or 'OKP'
 *
 * @param decodedToken - The decoded JWT token containing header, payload, signature, and raw parts
 * @param jwkData - The Auth0 JWKS data containing the array of JSON Web Keys
 * @returns Promise<boolean> - Returns true if any key validates the signature, false otherwise
 */
export async function isValidJwtSignature(decodedToken, jwkData) {
    if (!jwkData) {
        return false;
    }
    if (jwkData.keys.length === 0) {
        return false;
    }
    const jwks = jwkData.keys
        .filter(({ use }) => use === 'sig' || use === undefined)
        .filter(({ kty }) => kty === 'RSA' || kty === 'EC' || kty === 'OKP');
    if (jwks.length === 0) {
        return false;
    }
    const encoder = new TextEncoder();
    const data = encoder.encode([decodedToken.raw.header, decodedToken.raw.payload].join('.'));
    const signature = new Uint8Array(Array.from(decodedToken.signature).map((c) => c.charCodeAt(0)));
    for (const jwk of jwks) {
        try {
            const key = await crypto.subtle.importKey('jwk', jwk, { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' }, false, ['verify']);
            const isValid = await crypto.subtle.verify('RSASSA-PKCS1-v1_5', key, signature, data);
            if (isValid) {
                return true;
            }
        }
        catch (error) {
            console.error(error);
        }
    }
    return false;
}
//# sourceMappingURL=jwt.js.map